/**
 * Tenant config API client (SPEC G1 — booking-event-integration, R2).
 *
 * Reads the authenticated user's tenant config via GET /api/tenant/config. The
 * endpoint returns only NON-secret fields — dashboard_config + cal_embed_url (the
 * public Cal.com scheduling link the book-on-behalf modal embeds). The
 * cal_webhook_secret is NEVER returned here.
 *
 * Follows the existing fetch + Firebase-ID-token auth pattern from
 * lib/api/pipeline.ts.
 */

import { auth } from '../firebase';

const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  'https://sales-api-768596480524.us-central1.run.app';

export interface TenantConfig {
  dashboard_config: Record<string, unknown>;
  /** Public Cal.com scheduling link for the book-on-behalf embed (R2). */
  cal_embed_url?: string | null;
}

async function getAuthHeader(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }
  const token = await user.getIdToken();
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

/** GET /api/tenant/config — dashboard_config + cal_embed_url for the caller's tenant. */
export async function getTenantConfig(): Promise<TenantConfig> {
  const headers = await getAuthHeader();
  const response = await fetch(`${API_BASE_URL}/api/tenant/config`, { headers });
  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `Failed to load tenant config: ${response.statusText}`);
  }
  return response.json();
}
